import React, { MouseEvent, useEffect } from 'react';
import CalendarToday from '@mui/icons-material/CalendarToday';
import { Text, useTranslator } from '@eo-locale/react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import { classes } from '@guest-widgets/shared/src/classes';
import { WithClassName } from '@guest-widgets/shared/src/types/withClassName';
import { usePopupState, bindToggle, bindPopper } from 'material-ui-popup-state/hooks';
import { ids } from '@guest-widgets/shared/src/ids';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';

import { useFilters } from '../../../filtersContext/filtersContext';
import { DateRangePicker } from '../DateFilter/DateRangePicker';
import { DateChips } from '../DateFilter/DateChips';
import { FormatOption, useFormatFilterValue } from '../../../useFormatFilterValue';

import { Popper } from './Popper';
import { FilterTextField } from './FilterTextField';

export const Dates = ({ className }: WithClassName) => {
  const { translate } = useTranslator();
  const { dates: formatValue } = useFormatFilterValue();

  const {
    setNextDates,
    nextDates,
    setDates,
    filters: { dates },
  } = useFilters();

  const popupState = usePopupState({ variant: 'popper', popupId: 'dates-filters' });

  useEffect(() => {
    if (!popupState.isOpen) {
      //User clicked outside of calendar. Reset dates to previous state
      setNextDates(dates);
    }
  }, [popupState.isOpen]);

  const hasAppliedDate = !!nextDates;

  const handleSearchButtonClick = () => {
    setDates(nextDates);
    popupState.setOpen(false);
  };

  const handleClearButtonClick = (e: MouseEvent) => {
    if (popupState.isOpen) {
      //We should not close calendar by click on "clear" button
      e.stopPropagation();
    }
    setNextDates();
  };

  return (
    <>
      <Box className={className}>
        <StyledFilterTextField
          id={ids.catalog.datesFilter}
          popupState={popupState}
          className={`${classes.catalog.filters.dates.input} ${className || ''}`}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CalendarToday />
              </InputAdornment>
            ),
            endAdornment: hasAppliedDate ? (
              <StyledInputAdornment position="end">
                <IconButton
                  data-cy="dates-filter-close-button"
                  size="small"
                  onClick={handleClearButtonClick}
                >
                  <ClearOutlinedIcon />
                </IconButton>
              </StyledInputAdornment>
            ) : undefined,
          }}
          value={hasAppliedDate ? formatValue(nextDates, FormatOption.FullContent) : ''}
          placeholder={translate('product-catalog.filters.date.placeholder')}
          label={translate('product-catalog.filters.date.title')}
          {...bindToggle(popupState)}
        />
      </Box>

      <Popper
        className={classes.catalog.filters.dates.root}
        popperTitle="product-catalog.filters.date.title"
        placement="bottom"
        popupState={popupState}
        {...bindPopper(popupState)}
      >
        <Box width={336}>
          <DateChips onSelect={setNextDates} dates={nextDates} />
          <Divider />
          <DateRangePicker onSelect={setNextDates} dates={nextDates} />
          <SearchButtonContainer>
            <SearchButton
              variant={'contained'}
              disableElevation
              disableFocusRipple
              onClick={handleSearchButtonClick}
            >
              <Text id="product-catalog.filters.date.apply-dates" />
            </SearchButton>
          </SearchButtonContainer>
        </Box>
      </Popper>
    </>
  );
};

const StyledFilterTextField = styled(FilterTextField)({
  minWidth: '270px',
});

const StyledInputAdornment = styled(InputAdornment)(({ theme }) => ({
  marginRight: theme.spacing(-3),
  marginLeft: theme.spacing(-6),
}));

const SearchButtonContainer = styled(Box)(({ theme }) => ({
  borderTop: theme.border,
  margin: theme.spacing(0, -4, 3, -4),
  textAlign: 'right',
}));

const SearchButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(3, 3, -4, 0),
}));
